import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { RequireAccess } from '../auth/RequireAccess';

/**
 * API docs (D-settings-s1, Phase 9 S1), the lightest piece: a STATIC internal
 * reference of the admin API route groups (endpoint groupings, request / response
 * shapes, example payloads). NO model, NO mutation. It rides in S1 to amortise the
 * foundation. Gated settings:read (any admin read; viewer reaches it via the
 * read-everywhere '*:read'). The real endpoints are the migration seam; this
 * mirrors the /admin/* surface the mock data provider stands in for today.
 */

interface Endpoint {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  summary: string;
}

interface EndpointGroup {
  name: string;
  blurb: string;
  endpoints: Endpoint[];
}

const GROUPS: readonly EndpointGroup[] = [
  {
    name: 'Catalog',
    blurb: 'Brands, markets, channels, tags and offline venues.',
    endpoints: [
      {
        method: 'GET',
        path: '/admin/stores',
        summary: 'List brands per market',
      },
      {
        method: 'PATCH',
        path: '/admin/stores/:id',
        summary: 'Edit a brand and its channels',
      },
      {
        method: 'GET',
        path: '/admin/offline-stores',
        summary: 'List brick-and-mortar venues',
      },
    ],
  },
  {
    name: 'Members',
    blurb: 'Member accounts, subscriptions and referrals.',
    endpoints: [
      { method: 'GET', path: '/admin/users', summary: 'List members' },
      { method: 'PATCH', path: '/admin/users/:id', summary: 'Edit a member' },
      {
        method: 'GET',
        path: '/admin/subscriptions',
        summary: 'List Plus subscriptions',
      },
    ],
  },
  {
    name: 'Money',
    blurb: 'Cashback transactions, cashout requests and the balance ledger.',
    endpoints: [
      {
        method: 'GET',
        path: '/admin/cashbacks',
        summary: 'List cashback transactions',
      },
      {
        method: 'GET',
        path: '/admin/cashouts',
        summary: 'List cashout requests',
      },
      {
        method: 'POST',
        path: '/admin/cashouts/:id/approve',
        summary: 'Authorise a payout',
      },
    ],
  },
  {
    name: 'Trust and safety',
    blurb: 'Fraud queue, suspensions, chargebacks and adjustments.',
    endpoints: [
      {
        method: 'GET',
        path: '/admin/fraud-queue',
        summary: 'List flagged members',
      },
      {
        method: 'POST',
        path: '/admin/users/:id/suspend',
        summary: 'Suspend with a reason',
      },
      {
        method: 'POST',
        path: '/admin/adjustments',
        summary: 'Record a signed ledger adjustment',
      },
    ],
  },
  {
    name: 'Compliance',
    blurb: 'DSAR, consent, sanctions, tax and erasure.',
    endpoints: [
      {
        method: 'GET',
        path: '/admin/dsar',
        summary: 'List data-subject requests',
      },
      {
        method: 'GET',
        path: '/admin/tax',
        summary: 'The derived 1099-K report',
      },
      {
        method: 'POST',
        path: '/admin/erasure/:id/execute',
        summary: 'Execute an erasure (super_admin)',
      },
    ],
  },
  {
    name: 'Settings',
    blurb: 'System config, feature flags and the Plus marketing flag.',
    endpoints: [
      {
        method: 'GET',
        path: '/admin/settings',
        summary: 'Read the per-market config',
      },
      {
        method: 'PATCH',
        path: '/admin/settings/:market',
        summary: 'Edit a market config row',
      },
      {
        method: 'PATCH',
        path: '/admin/feature-flags/:id',
        summary: 'Toggle a flag with a reason',
      },
    ],
  },
  {
    name: 'Audit',
    blurb: 'The append-only audit trail (super_admin and compliance).',
    endpoints: [
      { method: 'GET', path: '/admin/audit', summary: 'Query the audit log' },
    ],
  },
];

const METHOD_COLOR: Record<
  Endpoint['method'],
  'success' | 'primary' | 'warning' | 'error'
> = {
  GET: 'success',
  POST: 'primary',
  PATCH: 'warning',
  DELETE: 'error',
};

const EXAMPLE_PAYLOAD = `PATCH /admin/feature-flags/12
{
  "enabled": true,
  "reason": "Rolled out to UK after the closed beta"
}

200 OK
{
  "id": 12,
  "key": "new-dashboard",
  "market": "uk",
  "enabled": true,
  "updatedAt": "2026-07-02T09:00:00.000Z"
}`;

/** The static reference itself. Exported for testing without the access wrapper. */
export const ApiDocsView = () => (
  <Box sx={{ maxWidth: 900 }} data-testid="api-docs">
    <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
      API docs
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Internal reference for the admin API route groups. Read-only; the real
      endpoints are the migration seam behind the mock data provider.
    </Typography>

    <Stack spacing={2}>
      {GROUPS.map((group) => (
        <Card key={group.name}>
          <CardContent>
            <Typography variant="subtitle1">{group.name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {group.blurb}
            </Typography>
            <Stack spacing={0.5}>
              {group.endpoints.map((ep) => (
                <Stack
                  key={ep.path + ep.method}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Chip
                    size="small"
                    label={ep.method}
                    color={METHOD_COLOR[ep.method]}
                    variant="outlined"
                  />
                  <Typography
                    variant="body2"
                    component="code"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {ep.path}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ep.summary}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardContent>
          <Typography variant="subtitle1">Example payload</Typography>
          <Divider sx={{ my: 1 }} />
          <Typography
            component="pre"
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              m: 0,
            }}
          >
            {EXAMPLE_PAYLOAD}
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  </Box>
);

/** Gated entry point mounted at /api-docs. Any admin with a read capability. */
export const ApiDocs = () => (
  <RequireAccess capability="settings:read">
    <ApiDocsView />
  </RequireAccess>
);
